import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../../services/api';

/**
 * Step2Review
 *
 * Shows computed stat chips, a verify step, and Save / Save Changes.
 * No material plan — that happens in SendToProductionModal.
 */
const StatChip = ({ label, value, unit = 'gm' }) => (
    <div className="bg-gray-50 rounded-lg px-3 py-2 text-center">
        <p className="text-xs text-gray-500 font-medium mb-0.5">{label}</p>
        <p className="text-sm font-bold text-gray-800">
            {typeof value === 'number' ? value.toFixed(3) : '—'}
            <span className="text-xs font-normal text-gray-400 ml-1">{unit}</span>
        </p>
    </div>
);

const Step2Review = ({
    computed,
    processData,
    selectedClient,
    existingSheetId,   // set in edit mode
    verified,
    onVerify,
    onUnverify,
    onSaved,
    onBack,
}) => {
    const [saving, setSaving]         = useState(false);
    const [savedSheet, setSavedSheet] = useState(null);
    const isEditMode = !!existingSheetId;

    const buildPayload = () => ({
        inputs:     { ...processData },
        clientId:   selectedClient._id,
        clientName: selectedClient.name,
    });

    const handleSave = async () => {
        setSaving(true);
        try {
            let sheet;
            if (isEditMode) {
                const res = await api.put(`/process-sheets/${existingSheetId}`, buildPayload());
                sheet = res.data;
                toast.success(`Sheet updated — ${sheet.sheetNumber}`);
            } else {
                const res = await api.post('/process-sheets', buildPayload());
                sheet = res.data;
                toast.success(`Sheet saved — ${sheet.sheetNumber}`);
            }
            setSavedSheet(sheet);
            onSaved?.(sheet);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save sheet');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-5">

            {/* ── Computed summary ──────────────────────────────────── */}
            <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Computed Values
                </p>
                <div className="grid grid-cols-2 gap-2">
                    <StatChip label="Abrasive / batch" value={computed.totalAbrasiveWeight} />
                    <StatChip label="Bond / batch"     value={computed.pureBondWeight} />
                    <StatChip label="Kryolite"         value={computed.kryoliteWeight} />
                    <StatChip label="Total stones"     value={computed.totalStones} unit="pcs" />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                    <StatChip label="Pre-pressure P1"  value={computed.prePressure}  unit="bar" />
                    <StatChip label="Main-pressure P2" value={computed.mainPressure} unit="bar" />
                    <StatChip label="Temperature"      value={computed.sinteringTemperature} unit="°C" />
                    <StatChip label="Volume / cup"     value={computed.cupVolumeInCubicCm}   unit="cm³" />
                </div>
            </div>

            {/* ── Verify ───────────────────────────────────────────── */}
            {!verified && (
                <button type="button" onClick={onVerify}
                    className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold text-sm tracking-wide transition-colors shadow-sm">
                    ✓ Verify Sheet
                </button>
            )}

            {verified && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <span className="text-emerald-600 text-sm font-semibold">✓ Verified</span>
                    <button type="button" onClick={onUnverify}
                        className="ml-auto text-xs text-gray-400 hover:text-gray-600 transition-colors">
                        Re-edit
                    </button>
                </div>
            )}

            {/* ── Save ─────────────────────────────────────────────── */}
            {verified && (
                <button type="button" onClick={handleSave}
                    disabled={saving || !!savedSheet}
                    className="w-full py-2.5 bg-gray-900 hover:bg-gray-700 disabled:opacity-40 text-white rounded-lg font-semibold text-sm transition-colors shadow-sm">
                    {saving
                        ? 'Saving…'
                        : savedSheet
                            ? `✓ Saved — ${savedSheet.sheetNumber}`
                            : isEditMode ? '💾 Save Changes' : '💾 Save Sheet'
                    }
                </button>
            )}

            {/* ── Back ─────────────────────────────────────────────── */}
            <button type="button" onClick={onBack}
                className="w-full py-2 text-gray-400 hover:text-gray-600 text-sm transition-colors">
                ← Back to Inputs
            </button>
        </div>
    );
};

export default Step2Review;